/**
 * providers.ts — service-provider directory (Phase 3D).
 *
 * Standalone tab for browsing DSCR lenders, property managers, insurance
 * agents, contractors, inspectors, and 1031 intermediaries. The in-detail
 * CTAs (Phase 3C) filter by type; this screen lets investors browse the
 * full directory with type + market filters.
 */

import { api } from './api.js';
import { escapeHtml, showToast } from './ui.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

// ─── Type labels + filters ──────────────────────────────────────────────

const TYPE_OPTIONS: { value: string; label: string; icon: string }[] = [
  { value: '', label: 'All providers', icon: 'users' },
  { value: 'dscr_lender', label: 'DSCR Lenders', icon: 'briefcase' },
  { value: 'hard_money_lender', label: 'Hard Money', icon: 'zap' },
  { value: 'property_manager', label: 'Property Managers', icon: 'key' },
  { value: 'insurance_agent', label: 'Insurance', icon: 'shield' },
  { value: 'contractor', label: 'Contractors', icon: 'tool' },
  { value: 'inspector', label: 'Inspectors', icon: 'check-square' },
  { value: '1031_intermediary', label: '1031 Exchange', icon: 'refresh-cw' },
];

interface Filters {
  type: string;
  market: string;
}
const filters: Filters = { type: '', market: '' };

// ─── Render ─────────────────────────────────────────────────────────────

export function renderProviders(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in screen-padded providers-screen';
  div.innerHTML = `
    <div class="screen-header">
      <div>
        <h1>Providers</h1>
        <div class="providers-subtitle">Vetted lenders, PMs, and more</div>
      </div>
    </div>

    <div class="providers-filters">
      <div class="providers-chip-row" id="providers-chip-row">
        ${TYPE_OPTIONS.map((t) => `
          <button type="button" class="providers-chip${filters.type === t.value ? ' selected' : ''}" data-type="${t.value}">
            <i data-feather="${t.icon}" width="14"></i>
            <span>${t.label}</span>
          </button>
        `).join('')}
      </div>
      <div class="providers-market-row">
        <input type="text" id="providers-market-input" placeholder="Market (e.g. Houston)" value="${escapeHtml(filters.market)}">
        <button class="btn btn-secondary btn-sm" onclick="applyProvidersFilter()">Filter</button>
      </div>
    </div>

    <div id="providers-list">
      <div style="color:var(--text-secondary);font-size:13px;padding:12px 0">Loading...</div>
    </div>
  `;
  setTimeout(() => {
    wireChips();
    loadProviders();
    if (window.feather) window.feather.replace();
  }, 50);
  return div;
}

function wireChips(): void {
  document.querySelectorAll('.providers-chip').forEach((el) => {
    el.addEventListener('click', () => {
      filters.type = el.getAttribute('data-type') || '';
      document.querySelectorAll('.providers-chip').forEach((c) => c.classList.remove('selected'));
      el.classList.add('selected');
      loadProviders();
    });
  });
}

export function applyProvidersFilter(): void {
  const v = (document.getElementById('providers-market-input') as HTMLInputElement | null)?.value?.trim() || '';
  filters.market = v;
  loadProviders();
}

export async function loadProviders(): Promise<void> {
  const el = document.getElementById('providers-list');
  if (!el) return;
  const qs = new URLSearchParams();
  if (filters.type) qs.set('type', filters.type);
  if (filters.market) qs.set('market', filters.market);
  try {
    const data = await api<AnyRecord>('GET', '/providers' + (qs.toString() ? '?' + qs.toString() : ''));
    const items = (data.items as AnyRecord[]) || [];
    if (items.length === 0) {
      el.innerHTML = `
        <div class="providers-empty">
          <i data-feather="users" width="48"></i>
          <h3>No providers match</h3>
          <p>Try a different type or market. We add new partners every week.</p>
        </div>
      `;
      if (window.feather) window.feather.replace();
      return;
    }
    el.innerHTML = items.map(renderProviderRow).join('');
    if (window.feather) window.feather.replace();
  } catch (err: any) {
    el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px;padding:12px 0">${escapeHtml(err.message || 'Failed to load providers')}</div>`;
  }
}

function renderProviderRow(p: AnyRecord): string {
  const id = escapeHtml(String(p.id));
  const name = escapeHtml(String(p.name || ''));
  const company = escapeHtml(String(p.company || ''));
  const type = String(p.provider_type || '');
  const typeLabel = TYPE_OPTIONS.find((o) => o.value === type)?.label || type;
  const markets = Array.isArray(p.markets) ? p.markets.join(' · ') : '';
  const rating = p.rating != null ? Number(p.rating).toFixed(1) : null;
  const reviews = p.review_count || 0;
  const desc = p.description ? escapeHtml(String(p.description)) : '';

  return `
    <div class="provider-row">
      <div class="provider-row-head">
        <div class="provider-row-title">
          <div class="provider-row-name">${name}</div>
          ${company ? `<div class="provider-row-company">${company}</div>` : ''}
          <div class="provider-row-type">${escapeHtml(typeLabel)}</div>
        </div>
        ${rating ? `<div class="provider-row-rating">★ ${rating}<span>(${reviews})</span></div>` : ''}
      </div>
      ${markets ? `<div class="provider-row-markets">${escapeHtml(markets)}</div>` : ''}
      ${desc ? `<div class="provider-row-desc">${desc}</div>` : ''}
      <div class="provider-row-actions">
        <button class="btn btn-primary btn-sm" onclick="requestProviderIntroFromList('${id}')">
          <i data-feather="send" width="13"></i>
          <span>Request intro</span>
        </button>
      </div>
    </div>
  `;
}

export async function requestProviderIntroFromList(providerId: string): Promise<void> {
  try {
    const resp = await api<AnyRecord>('POST', `/providers/${providerId}/referral`, {
      referral_type: 'introduction',
    });
    showToast(String(resp.message || 'Intro requested'), 'success');
  } catch (err: any) {
    showToast(err.message || 'Could not request intro', 'error');
  }
}
